'use client';

import { useEffect } from 'react';

/**
 * Global error boundary - catches errors in the root layout
 * Must render its own html and body tags
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('Global error:', error);
  }, [error]);

  return (
    <html lang="en">
      <head>
        <style>{`
          :root {
            --bg-color: #EAE8E3;
            --text-color: #1F1C1B;
            --border-color: #1F1C1B;
            --font-display: 'Antonio', sans-serif;
            --font-body: 'Inter', sans-serif;
            --spacing-sm: 16px;
            --spacing-md: 24px;
            --spacing-lg: 40px;
          }
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: var(--font-body);
            background-color: var(--bg-color);
            color: var(--text-color);
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: var(--spacing-md);
          }
          
          .global-error-container {
            max-width: 600px;
            width: 100%;
            border: 2px solid var(--border-color);
            padding: var(--spacing-lg);
            text-align: center;
          }
          
          .error-icon {
            width: 80px;
            height: 80px;
            border: 2px solid var(--border-color);
            margin: 0 auto var(--spacing-md);
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .error-title {
            font-family: var(--font-display);
            font-size: clamp(28px, 5vw, 40px);
            text-transform: uppercase;
            line-height: 1;
            margin-bottom: var(--spacing-sm);
          }
          
          .error-message {
            font-size: 14px;
            opacity: 0.7;
            margin-bottom: var(--spacing-md);
            line-height: 1.5;
          }
          
          .error-id {
            font-size: 11px;
            opacity: 0.5;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: var(--spacing-md);
            font-family: monospace;
          }
          
          .error-button {
            padding: 12px 24px;
            background-color: var(--text-color);
            color: var(--bg-color);
            border: 1px solid var(--border-color);
            font-size: 12px;
            text-transform: uppercase;
            font-weight: 500;
            letter-spacing: 0.5px;
            cursor: pointer;
            transition: all 0.2s ease;
            font-family: var(--font-body);
          }
          
          .error-button:hover {
            background-color: transparent;
            color: var(--text-color);
            outline: 2px solid var(--text-color);
          }
        `}</style>
      </head>
      <body>
        <div className="global-error-container">
          <div className="error-icon">
            <svg 
              width="40" 
              height="40" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          
          <h1 className="error-title">
            Critical Error
          </h1>
          
          <p className="error-message">
            A critical error occurred. Please try refreshing the page.
          </p>

          {error.digest && (
            <p className="error-id">
              Error ID: {error.digest}
            </p>
          )}

          <button
            onClick={reset}
            className="error-button"
          >
            Try Again
          </button>
        </div>
      </body>
    </html>
  );
}
